import {formatMoney} from 'accounting'
import cn from 'classnames'
import {FC, useEffect, useState} from 'react'

import {InfoBanner} from '@/components/common/banner/InfoBanner'
import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {usePreferences} from '@/components/context/Preferences'
import {useAffiliateContent} from '@/content/affiliate'
import {Trans, useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {FORM_CONTAINER_PADDING, FORM_CONTAINER_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {affiliateConfig} from '@/utils/affiliate/config'
import {getFormattingOptions} from '@/utils/currency'
import {useLanguage} from '@/utils/i18n/language'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {SnackbarType} from '../snackbar/constants'
import {useSnackbarContext} from '../snackbar/context/SnackbarContext'
import {TransLink} from '../TransLink'

type AffiliateBannerProps = {
  isDiscountApplied: boolean
  fullWidth?: boolean
  onClose?: () => void
  trackedEventParameters?: Record<string, string>
  hasAnimationDelay?: boolean
}

export const AffiliateBanner: FC<AffiliateBannerProps> = ({
  isDiscountApplied,
  fullWidth = false,
  onClose,
  trackedEventParameters,
  hasAnimationDelay = true,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const {affiliate, currency} = usePreferences()
  const {
    affiliateDiscountList,
    generalBannerTitle,
    generalBannerDescription,
    staticPriceBannerDescription,
    closeButtonText,
    automaticallyAppliedDiscountDescription,
  } = useAffiliateContent()
  const [isAffiliateBannerVisible, setIsAffiliateBannerVisible] = useState(false)
  const {showSnackbar} = useSnackbarContext()

  useEffect(() => {
    if (affiliate) {
      if (hasAnimationDelay) {
        // Show affiliate banner after 2 seconds
        setTimeout(() => setIsAffiliateBannerVisible(true), 2000)
      } else {
        setIsAffiliateBannerVisible(true)
      }

      analytics.track(`Affiliate banner for ${affiliate} is visible`, trackedEventParameters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (affiliate && isDiscountApplied) {
      showSnackbar(t(automaticallyAppliedDiscountDescription), true, SnackbarType.success)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiscountApplied])

  if (!affiliate) {
    return null
  }

  const affiliateContent = affiliateDiscountList[affiliate]
  const affiliateDiscount = affiliateConfig[affiliate]

  const title = affiliateContent?.bannerTitle ? affiliateContent.bannerTitle : generalBannerTitle

  const description = affiliateContent?.bannerDescription
    ? affiliateContent.bannerDescription
    : affiliateDiscount.discountPrice
    ? staticPriceBannerDescription
    : generalBannerDescription

  const formattedAffiliateDiscountPrice = affiliateDiscount.discountPrice
    ? formatMoney(affiliateDiscount.discountPrice, getFormattingOptions(currency, language, false))
    : 0

  return (
    <>
      <div
        className={cn(
          'affiliate',
          isAffiliateBannerVisible && 'visible',
          fullWidth && 'full-width',
        )}
      >
        <InfoBanner
          fullWidth={fullWidth}
          imageSrc={affiliateContent.imageSrc}
          imageAlt={affiliateContent.imageAlt}
          imageWidth={affiliateContent.imageWidth}
          imageHeight={affiliateContent.imageHeight}
          title={t(title, {name: affiliateContent.name})}
          description={
            <Trans
              i18nKey={description}
              values={{
                name: affiliateContent.name,
                percentage: affiliateDiscount.discountPercentage,
                code: affiliateDiscount.discountCoupon,
                price: formattedAffiliateDiscountPrice,
              }}
            >
              text
              {affiliateContent?.bannerLinkHref && (
                <TransLink href={affiliateContent.bannerLinkHref}>text</TransLink>
              )}
            </Trans>
          }
          button={
            <Button
              variant={ButtonVariant.tertiary}
              type="button"
              size={ButtonSize.small}
              onClick={() => {
                setIsAffiliateBannerVisible(false)
                onClose && onClose()
                analytics.track(
                  `Affiliate banner for ${affiliate} is closed`,
                  trackedEventParameters,
                )
              }}
              fullWidth
            >
              {t(closeButtonText)}
            </Button>
          }
          backgroundColor={theme.palette.info.light}
        />
      </div>

      <style jsx>{`
        .affiliate {
          visibility: hidden;
          opacity: 0;
          max-height: 0;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }
        .visible {
          visibility: visible;
          opacity: 1;
          max-height: 320px;
        }
        .full-width.visible {
          padding-top: ${theme.spacing[2]};
          padding-bottom: ${theme.spacing[2]};
        }
        @media ${TABLET_SCREENSIZE} {
          .full-width {
            padding: 0 ${theme.spacing[3]};
          }
          .full-width.visible {
            padding-top: ${theme.spacing[2]};
            padding-bottom: ${theme.spacing[2]};
            background-color: ${theme.palette.info.light};
          }
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .affiliate {
            margin: 0 -${FORM_CONTAINER_PADDING};
            padding: 0 ${theme.spacing[3]};
            background-color: ${theme.palette.info.light};
          }
          .visible {
            padding: ${theme.spacing[3]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .affiliate {
            padding: 0;
            margin: 0 -${FORM_CONTAINER_PADDING_MOBILE};
          }
          .visible {
            padding: ${theme.spacing[2]} 0;
          }
          .full-width {
            padding: 0;
          }
        }
      `}</style>
    </>
  )
}
